// Read-only helpers for the Task Center (T2 of Tasks v2). The Task
// Center is fully transparent — every authenticated user sees every
// task_instances row via the new authenticated_select policy from
// mig 053. T2 ships no mutations; this module exposes ONLY SELECT
// helpers.
//
// Mutations (complete / create / due-date edit / assign / delete /
// system-generate) live in src/lib/tasksCenterMutationsApi.js which
// landed with T3+ as each functional tab arrived. Keeping reads and
// writes in separate modules makes the static no-mutation lock for
// the read-only tabs trivial: the lock asserts that none of those
// files import from the mutations module.

import {TASKS_PUBLIC_ASSIGNEE_AVAILABILITY_KEY, visiblePublicAssignees} from './tasks.js';
import type {SupabaseClient} from './supabaseTypes.js';

// Route and shell readers may hand TanStack Query's cancellation signal down
// to a real PostgREST builder. Small test fakes often settle as plain promises,
// so capability-check at the transport seam rather than requiring abortSignal
// everywhere a read helper is called.
type MaybeAbortable<Request> = {then: unknown; abortSignal?: (signal: AbortSignal) => Request};

function withSignal<Request extends MaybeAbortable<Request>>(request: Request, signal?: AbortSignal): Request {
  return signal && typeof request.abortSignal === 'function' ? request.abortSignal(signal) : request;
}

// ── Types ─────────────────────────────────────────────────────────────────
// Every row here comes from `select('*')` (or a named column list) against
// tables with no generated database types, so each shape names ONLY the columns
// these helpers read and carries the rest as `unknown`.

// {id, full_name} — the ONLY two fields list_eligible_assignees returns, by
// design (see loadEligibleProfilesById: role and email never cross the wire).
export type TaskProfile = {id: string; full_name?: string | null};

export type TaskInstanceRow = {
  id?: string;
  status?: string | null;
  title?: string | null;
  due_date?: string | null;
  assignee_profile_id?: string | null;
  submission_source?: string | null;
  submitted_by_team_member?: string | null;
  created_by_display_name?: string | null;
  from_recurring_template?: unknown;
  from_system_rule_id?: string | null;
  template_id?: string | null;
  request_photo_path?: string | null;
  completion_photo_path?: string | null;
  [column: string]: unknown;
};

export type TaskTemplateRow = {id?: string; [column: string]: unknown};
export type TaskSystemRuleRow = {id?: string; [column: string]: unknown};

// `name` is nullable because it is the profile's own full_name when one
// resolves; only the two fallbacks ('Unknown user' / 'Unassigned') are
// guaranteed strings.
export type TaskAssigneeGroup = {
  profileId: string | null;
  name: string | null | undefined;
  tasks: TaskInstanceRow[];
};
export type TaskTemplateBucket = {template: TaskTemplateRow; openCount: number; instances: TaskInstanceRow[]};
export type TaskSystemRuleBucket = {rule: TaskSystemRuleRow; openCount: number; instances: TaskInstanceRow[]};

/**
 * Load every eligible assignee for the My Tasks tab's grouped view.
 * Returns an id-keyed map of {id, full_name} for O(1) name resolution
 * per row.
 *
 * Goes through the SECURITY DEFINER list_eligible_assignees RPC (mig
 * 041) instead of a direct profiles SELECT so the call works for
 * regular non-admin users regardless of profiles RLS, and so we never
 * leak role/email through the wire (the RPC returns id + full_name
 * only). Same RPC the public Tasks webform uses.
 */
export async function loadEligibleProfilesById(sb: SupabaseClient): Promise<Record<string, TaskProfile>> {
  const {data, error} = await sb.rpc('list_eligible_assignees');
  if (error) throw new Error(`loadEligibleProfilesById: ${error.message}`);
  const out: Record<string, TaskProfile> = {};
  const rows: TaskProfile[] = data || [];
  for (const p of rows) out[p.id] = {id: p.id, full_name: p.full_name};
  return out;
}

/**
 * Load the assignable-only subset of eligible profiles for Task Center
 * write paths (NewTask / Reassign / Recurring template / System rule
 * edit dropdowns). This applies the same Public Tasks assignee
 * availability filter (`webform_config.tasks_public_assignee_availability`
 * → `{hiddenProfileIds: [...]}`) that /webforms/tasks already uses, so a
 * profile hidden via Team Availability → Public Tasks is excluded from
 * Task Center mutation dropdowns too.
 *
 * Hidden profiles still appear in the unfiltered display map
 * (loadEligibleProfilesById) so existing tasks/templates/rules already
 * assigned to a hidden profile continue rendering the person's name in
 * read-only rows. "Hidden" means "not assignable going forward," not
 * "Unknown user everywhere."
 *
 * Read path: list_eligible_assignees RPC + webform_config row → filter
 * via visiblePublicAssignees(). Never reads profiles directly.
 *
 * Failure modes (Codex amendment — must fail CLOSED for hidden filtering):
 *   - row missing / null  → defaults to "no hiddenProfileIds" → every
 *                           eligible profile is assignable. The
 *                           availability config is opt-in; absent config
 *                           means nobody is hidden.
 *   - webform_config read returns an error or throws → return an EMPTY
 *                           assignable map. We do NOT fall back to the
 *                           unfiltered eligible list because a transient
 *                           read failure would silently re-expose hidden
 *                           profiles in mutation dropdowns. Empty map
 *                           means admins see "— Select —" and can retry
 *                           by reopening the modal.
 */
export async function loadTaskAssignableProfilesById(sb: SupabaseClient): Promise<Record<string, TaskProfile>> {
  const {data: rpcData, error: rpcErr} = await sb.rpc('list_eligible_assignees');
  if (rpcErr) throw new Error(`loadTaskAssignableProfilesById: ${rpcErr.message}`);
  const eligible: TaskProfile[] = (rpcData || []).map((p: TaskProfile) => ({id: p.id, full_name: p.full_name}));

  let availability: unknown = null;
  try {
    const {data: row, error: rowErr} = await sb
      .from('webform_config')
      .select('data')
      .eq('key', TASKS_PUBLIC_ASSIGNEE_AVAILABILITY_KEY)
      .maybeSingle();
    if (rowErr) {
      // Fail closed: a read error must not silently re-expose hidden
      // profiles. Return empty so the dropdown shows nothing rather
      // than the full unfiltered list.
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('loadTaskAssignableProfilesById availability read failed:', rowErr.message);
      }
      return {};
    }
    if (row && row.data) {
      availability = row.data;
    }
  } catch (e) {
    if (typeof console !== 'undefined' && console.warn) {
      // `e` is a caught value, so `unknown`: only an object can carry `.message`,
      // and a non-object contributed nothing to `e && e.message` before either.
      console.warn(
        'loadTaskAssignableProfilesById availability read threw:',
        typeof e === 'object' && e !== null ? (e as {message?: unknown}).message : undefined,
      );
    }
    return {};
  }

  const visible = visiblePublicAssignees(eligible, availability);
  const out: Record<string, TaskProfile> = {};
  for (const p of visible) out[p.id] = {id: p.id, full_name: p.full_name};
  return out;
}

/**
 * Pure helper: split a flat open-tasks list into
 *   { mine: TaskRow[], otherGroups: { profileId, name, tasks }[] }
 * for the My Tasks tab. mine is everything assigned to callerProfileId;
 * other groups are keyed by assignee_profile_id and named via the
 * profiles map (falling back to 'Unassigned' for null assignees and
 * to 'Unknown user' for ids not in the profiles map). Both lists keep
 * the input sort order (caller passes due_date asc).
 */
export function splitTasksForMyTab(
  allOpenTasks: TaskInstanceRow[] | null | undefined,
  callerProfileId: string | null | undefined,
  profilesById: Record<string, TaskProfile>,
): {mine: TaskInstanceRow[]; otherGroups: TaskAssigneeGroup[]} {
  const mine: TaskInstanceRow[] = [];
  const grouped = new Map<string, TaskAssigneeGroup>();
  for (const ti of allOpenTasks || []) {
    if (ti.assignee_profile_id && ti.assignee_profile_id === callerProfileId) {
      mine.push(ti);
      continue;
    }
    const key = ti.assignee_profile_id || '__unassigned__';
    if (!grouped.has(key)) {
      const profile = ti.assignee_profile_id ? profilesById[ti.assignee_profile_id] : null;
      const name = ti.assignee_profile_id ? (profile ? profile.full_name : 'Unknown user') : 'Unassigned';
      grouped.set(key, {profileId: ti.assignee_profile_id || null, name, tasks: []});
    }
    // `?.` rather than an assertion: the set above guarantees the entry, and
    // optional chaining states that without claiming anything the checker
    // cannot see.
    grouped.get(key)?.tasks.push(ti);
  }
  // Stable group order: by display name asc.
  const otherGroups = Array.from(grouped.values()).sort((a, b) => {
    const an = (a.name || '').toLowerCase();
    const bn = (b.name || '').toLowerCase();
    return an < bn ? -1 : an > bn ? 1 : 0;
  });
  return {mine, otherGroups};
}

/**
 * Pure helper: derive the "submitted/created" attribution string for
 * a task row. Public-webform rows surface the operator's free-text
 * name; logged-in-created rows surface the locked creator name from
 * mig 050. Recurring + system rows surface their generator label.
 *
 * Returns null when no attribution applies (legacy v1 generated rows
 * with no display data).
 */
export function attributionFor(ti: TaskInstanceRow | null | undefined): {label: string; name: string} | null {
  if (!ti) return null;
  if (ti.submission_source === 'public_webform' && ti.submitted_by_team_member) {
    return {label: 'Submitted by', name: ti.submitted_by_team_member};
  }
  if (ti.created_by_display_name) {
    return {label: 'Created by', name: ti.created_by_display_name};
  }
  if (ti.from_recurring_template) {
    return {label: 'Source', name: 'Recurring template'};
  }
  if (ti.from_system_rule_id) {
    return {label: 'Source', name: 'System rule'};
  }
  return null;
}

/**
 * Pure helper: classify a task's due date relative to today (caller
 * passes a 'YYYY-MM-DD' string for today so tests are deterministic).
 * Returns 'overdue' | 'today' | 'upcoming'.
 */
export function dueStateFor(
  ti: TaskInstanceRow | null | undefined,
  todayStr: string,
): 'overdue' | 'today' | 'upcoming' {
  if (!ti || !ti.due_date) return 'upcoming';
  if (ti.due_date < todayStr) return 'overdue';
  if (ti.due_date === todayStr) return 'today';
  return 'upcoming';
}

/**
 * Pure helper: photo-presence indicator for a row. Reads the legacy
 * single-path columns (request_photo_path / completion_photo_path)
 * already on the row — no extra query against the sidecar in T2.
 */
export function photoPresenceFor(ti: TaskInstanceRow | null | undefined): {
  hasRequest: boolean;
  hasCompletion: boolean;
} {
  if (!ti) return {hasRequest: false, hasCompletion: false};
  return {
    hasRequest: !!ti.request_photo_path,
    hasCompletion: !!ti.completion_photo_path,
  };
}

// ── Tasks v2 T3: Header badge count ─────────────────────────────────────
//
// Count open tasks assigned to the caller whose due_date is today or
// earlier (overdue + due-today). Filters server-side so only the
// caller's eligible rows transit, even though the v2 transparency RLS
// would otherwise let the caller SELECT every row. The Header badge
// uses this number; soft-fail on the consumer side keeps Header
// rendering even if the DB call errors.
//
// Returns 0 for a falsy callerProfileId (unauthenticated render path).
export async function countMyOpenDueOrPastTasks(
  sb: SupabaseClient,
  callerProfileId: string | null | undefined,
  todayStr: string | null | undefined,
  signal?: AbortSignal,
): Promise<number> {
  if (!callerProfileId) return 0;
  if (!todayStr) return 0;
  const request = sb
    .from('task_instances')
    .select('id', {count: 'exact', head: true})
    .eq('status', 'open')
    .eq('assignee_profile_id', callerProfileId)
    .lte('due_date', todayStr);
  const {count, error} = await withSignal(request, signal);
  if (error) throw new Error(`countMyOpenDueOrPastTasks: ${error.message}`);
  return count || 0;
}

// ── Tasks v2 T4: Completed tab ──────────────────────────────────────────
//
// Read the most recent completed task_instances rows. Capped at 200 so
// a long-running farm doesn't pull thousands of rows into the browser
// for a read-only review pane; foreseeable cohort fits comfortably.
// Older completions remain queryable via direct DB / future filters.
// ── Tasks v2 T4: Recurring tab ──────────────────────────────────────────
//
// Load every recurring task_templates row (active + inactive) so the
// Recurring tab can show toggle state. Inactive templates may still
// have open instances generated before deactivation; orphan grouping
// in the pure helper below covers the post-delete case (template_id
// went to NULL via mig 050's ON DELETE SET NULL).
/**
 * Pure helper: bucket open recurring instances under their parent
 * templates and surface orphans (designation='recurring' but
 * template_id is NULL — the parent was deleted via the v2
 * SET NULL FK). Returns:
 *   {
 *     templates: [{template, openCount, instances: TaskRow[]}, ...],
 *     orphans:   TaskRow[],
 *   }
 * Templates appear in the order they were passed in (caller already
 * sorted by active desc, title asc). Instances inside each bucket
 * keep the input sort order (caller passed due_date asc).
 */
export function groupRecurringByTemplate(
  templates: TaskTemplateRow[] | null | undefined,
  openInstances: TaskInstanceRow[] | null | undefined,
): {templates: (TaskTemplateBucket | undefined)[]; orphans: TaskInstanceRow[]} {
  const byId = new Map<string, TaskTemplateBucket>();
  for (const t of templates || []) {
    if (t && t.id) byId.set(t.id, {template: t, openCount: 0, instances: []});
  }
  const orphans: TaskInstanceRow[] = [];
  for (const ti of openInstances || []) {
    const tid = ti.template_id;
    // `has()` then `get()` reads as two independent lookups to the checker.
    // One `get()` is the same test — a bucket is never stored as undefined —
    // and it carries the narrowing the two writes below need.
    const bucket = tid ? byId.get(tid) : undefined;
    if (bucket) {
      bucket.instances.push(ti);
      bucket.openCount += 1;
    } else {
      orphans.push(ti);
    }
  }
  // The element type keeps `| undefined` because that is what Map.get can
  // return. The filter above it guarantees every id is present; this records
  // the API's contract rather than asserting past it.
  const templateBuckets = (templates || [])
    .filter((t): t is TaskTemplateRow & {id: string} => Boolean(t && t.id))
    .map((t) => byId.get(t.id));
  return {templates: templateBuckets, orphans};
}

// ── Tasks v2 T8: due-date edit history ─────────────────────────────────
//
// The task-interaction catalog owns the open-triggered history query. This
// shared type remains here because it documents the validated sidecar row
// consumed by both the catalog and the history panel.
export type DueDateEditHistoryRow = {
  id: string;
  instance_id?: string | null;
  edited_at?: string | null;
  edited_by_profile_id?: string | null;
  edited_by_role?: string | null;
  prior_due_date?: string | null;
  new_due_date?: string | null;
};

// ── Tasks v2 T6/T7: photo lightbox sidecar reader ──────────────────────
//
// The task-interaction catalog owns the open-triggered sidecar query and the
// signed-URL resources that consume these paths. This shared type is the one
// place the row shape is named, rather than duplicating it by component.
export type TaskInstancePhotoRow = {
  id: string;
  instance_id: string;
  kind: string;
  storage_path: string;
  sort_order: number;
  uploaded_at?: string | null;
  uploaded_by_profile_id?: string | null;
};

// ── Tasks v2 T5: System Tasks tab ───────────────────────────────────────
//
// Read every task_system_rules row (active + inactive). RLS allows
// authenticated SELECT on this table per mig 053; the System Tasks
// tab itself is admin-gated in TaskCenterView. The loader is shared
// safe for any authenticated caller.
// Read open task_instances generated by a system rule. Codex T5 lock:
// match designation='system' OR from_system_rule_id IS NOT NULL so
// any historical row that lacks the designation label still surfaces
// under its rule (the BEFORE INSERT trigger from mig 053 sets
// designation='system' for new rule-generated rows, but legacy paths
// may have written one without the other).
/**
 * Pure helper: bucket open system task instances under their owning
 * task_system_rules row by from_system_rule_id. Rows whose
 * from_system_rule_id is NULL or doesn't match any rule in the input
 * land in the orphans bucket so the UI can surface them rather than
 * silently dropping them.
 *
 * Returns:
 *   {
 *     rules:   [{rule, openCount, instances: TaskRow[]}, ...],
 *     orphans: TaskRow[],
 *   }
 *
 * Rules appear in the input order (caller sorts by active desc, name
 * asc). Instances inside each bucket keep the input sort order
 * (caller passes due_date asc).
 */
export function groupSystemTasksByRule(
  rules: TaskSystemRuleRow[] | null | undefined,
  openInstances: TaskInstanceRow[] | null | undefined,
): {rules: (TaskSystemRuleBucket | undefined)[]; orphans: TaskInstanceRow[]} {
  const byId = new Map<string, TaskSystemRuleBucket>();
  for (const r of rules || []) {
    if (r && r.id) byId.set(r.id, {rule: r, openCount: 0, instances: []});
  }
  const orphans: TaskInstanceRow[] = [];
  for (const ti of openInstances || []) {
    const rid = ti.from_system_rule_id;
    // Same one-lookup point as groupRecurringByTemplate above.
    const bucket = rid ? byId.get(rid) : undefined;
    if (bucket) {
      bucket.instances.push(ti);
      bucket.openCount += 1;
    } else {
      orphans.push(ti);
    }
  }
  const ruleBuckets = (rules || [])
    .filter((r): r is TaskSystemRuleRow & {id: string} => Boolean(r && r.id))
    .map((r) => byId.get(r.id));
  return {rules: ruleBuckets, orphans};
}
